<template>
  <div class="pa-1 mt-4">
    <!-- Summary Card -->
    <v-row class="mb-4">
      <v-col cols="12" sm="4">
        <v-card outlined class="ordera-box-shadow">
          <v-card-text>
            <div class="text-center">
              <div class="text-caption text--secondary mb-2">
                {{ $t("total_topups_amount") }}
              </div>
              <div class="text-h5 primary--text font-weight-bold">
                {{ formatAmount(grandTotal) }} {{ restaurantCurrency }}
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Desktop View -->
    <v-data-table
      v-if="!device.isMobile"
      :headers="headers"
      :items="creditTopupsWithTotal"
      class="striped-table ordera-box-shadow"
      hide-default-footer
      disable-pagination
      mobile-breakpoint="0"
      :loading="loading"
    >
      <template v-slot:no-data>
        <v-container class="text-center py-12">
          <v-icon size="64" color="grey lighten-1" class="mb-4">mdi-cash-off</v-icon>
          <div class="text-h6 grey--text text--darken-1 mb-2">
            {{ $t("no_data") }}
          </div>
          <div class="text-body-2 grey--text">
            {{ $t("select_date_and_filters") }}
          </div>
        </v-container>
      </template>

      <template v-slot:item.num="{ item }">
        <span :class="item.isTotal ? 'font-weight-bold' : ''">{{ item.num }}</span>
      </template>

      <template v-slot:item.date="{ item }">
        <span :class="item.isTotal ? 'font-weight-bold' : ''">
          {{ item.isTotal ? $t("total").toUpperCase() : localeDate(item.date) }}
        </span>
      </template>

      <template v-slot:item.user_name="{ item }">
        <span :class="item.isTotal ? 'font-weight-bold' : ''">{{ item.user_name }}</span>
      </template>

      <template v-slot:item.total_amount="{ item }">
        <span
          :class="
            item.isTotal ? 'font-weight-bold primary--text' : 'font-weight-bold success--text'
          "
        >
          {{ formatAmount(item.total_amount) }}
        </span>
      </template>
    </v-data-table>

    <!-- Mobile View -->
    <mobile-data-cards
      v-else
      :headers="mobileHeaders"
      :items="creditTopups"
      :loading="loading"
      item-key="id"
      :fields-per-row="2"
      :bold-fields="['user_name']"
      :show-row-number="false"
      class="px-3"
    >
      <template v-slot:item.date="{ item }">
        {{ localeDate(item.date) }}
      </template>
      <template v-slot:item.total_amount="{ item }">
        <span class="font-weight-bold success--text">
          {{ formatAmount(item.total_amount) }}
        </span>
      </template>
    </mobile-data-cards>
  </div>
</template>

<script>
import hasSnackbar from "@/mixins/hasSnackbar";
import limitService from "@/services/limit";
import MobileDataCards from "@/components/global/MobileDataCards.vue";
import hasPrinting from "@/mixins/hasPrinting";
import hasReportPrinting from "@/mixins/report/hasReportPrinting";
import hasExcel from "@/mixins/hasExcel";

export default {
  name: "CompanyMultiRestaurantTabCreditTopups",
  components: {
    MobileDataCards,
  },
  mixins: [hasSnackbar, hasPrinting, hasReportPrinting, hasExcel],
  data: () => ({
    creditTopups: [],
    loading: false,
  }),
  computed: {
    filters() {
      return this.$store.state.report.filters;
    },
    companyId() {
      return this.$store.state.company?.id || null;
    },
    grandTotal() {
      return this.creditTopups.reduce((sum, t) => sum + t.total_amount, 0);
    },
    creditTopupsWithTotal() {
      const topups = this.creditTopups.map((item, index) => ({
        ...item,
        num: index + 1,
        isTotal: false,
      }));
      if (topups.length > 0) {
        topups.push({
          id: "total",
          num: "",
          date: "",
          user_name: "",
          total_amount: this.grandTotal,
          isTotal: true,
        });
      }
      return topups;
    },
    headers() {
      return [
        {
          text: this.$t("r_b"),
          value: "num",
          sortable: false,
          width: "80px",
          print: false,
        },
        { text: this.$t("date"), value: "date", sortable: true },
        { text: this.$t("employee"), value: "user_name", sortable: false },
        {
          text: this.$t("amount") + ` (${this.restaurantCurrency})`,
          value: "total_amount",
          sortable: false,
          align: "right",
        },
      ];
    },
    mobileHeaders() {
      return [
        { text: this.$t("date"), value: "date" },
        { text: this.$t("employee"), value: "user_name" },
        {
          text: this.$t("amount") + ` (${this.restaurantCurrency})`,
          value: "total_amount",
        },
      ];
    },
  },
  methods: {
    parseAmount(value) {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    },
    normalizeCreditTransaction(item) {
      const userName = [item?.user?.name, item?.user?.lastname]
        .filter(Boolean)
        .join(" ");
      return {
        id: item?.id || null,
        date: item?.date || item?.created_at || null,
        user_name: userName || item?.user_name || "",
        total_amount: this.parseAmount(item?.total_amount),
        notes: item?.notes || "",
      };
    },
    async fetchTopups() {
      if (!this.companyId) return;
      this.loading = true;
      this.creditTopups = [];
      try {
        const { data } = await limitService.getCompanyTransactions(this.companyId);
        const userGroups = Array.isArray(data) ? data : [];
        this.creditTopups = userGroups
          .flatMap((group) => group.limitTransactions || [])
          .filter(
            (t) =>
              t.limit_type === "credit" &&
              t.company_id === this.companyId &&
              this.parseAmount(t.total_amount) > 0
          )
          .map((t) => this.normalizeCreditTransaction(t))
          .sort((a, b) => new Date(b.date) - new Date(a.date));
      } catch (error) {
        this.showErrorSnackbar(error, this.$t("error_loading_report"));
      } finally {
        this.loading = false;
      }
    },
    formatAmount(amount) {
      return (parseFloat(amount) || 0).toFixed(2);
    },
    getExportHeaders() {
      return this.headers.filter((h) => h.print !== false);
    },
    formatTopupForExport(item) {
      const exportItem = {};
      this.getExportHeaders().forEach((h) => {
        if (h.value === "date") exportItem[h.value] = this.localeDate(item.date);
        else if (h.value === "total_amount")
          exportItem[h.value] = this.formatAmount(item.total_amount);
        else exportItem[h.value] = item[h.value] || "";
      });
      return exportItem;
    },
  },
  watch: {
    "filters.dates": {
      handler() {
        this.fetchTopups();
      },
      deep: true,
    },
    isExcelDownloading() {
      if (!this.isExcelDownloading) return;
      this.downloadExcel({
        fileName: [this.$t("report"), this.$t("credit_topups")].join(" ") + ".xlsx",
        sheets: [
          {
            name: this.$t("credit_topups"),
            headers: this.getExportHeaders(),
            items: this.creditTopups.map((item) => this.formatTopupForExport(item)),
            title: [this.$t("report"), this.$t("credit_topups")].join(" "),
            subtitle: this.subtitle,
          },
        ],
      });
    },
    isPrinting(isPrinting) {
      if (!isPrinting) return;
      this.printDocument({
        tables: [
          {
            headers: this.getExportHeaders(),
            items: this.creditTopups.map((item) => this.formatTopupForExport(item)),
            showCaption: true,
          },
        ],
        title: [this.$t("report"), this.$t("credit_topups")].join(": "),
        subtitle: this.subtitle,
        hideDatetime: true,
      });
    },
  },
  mounted() {
    this.fetchTopups();
  },
};
</script>

